// Google Identity Services (the "Sign in with Google" button and Chrome's automatic
// prompt). Loaded only on the sign-in pages; ADR 0007.

const GSI_SCRIPT = "https://accounts.google.com/gsi/client";

export type GoogleCredentialResponse = { credential: string };

type GsiButtonOptions = {
  type?: "standard";
  theme?: "outline" | "filled_blue";
  size?: "large";
  shape?: "rectangular";
  text?: "signin_with" | "signup_with" | "continue_with";
  width?: number;
};

export type GsiId = {
  initialize(options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    use_fedcm_for_prompt?: boolean;
    context?: "signin" | "signup";
  }): void;
  renderButton(parent: HTMLElement, options: GsiButtonOptions): void;
  prompt(): void;
  disableAutoSelect(): void;
};

declare global {
  interface Window {
    google?: { accounts: { id: GsiId } };
  }
}

let loading: Promise<GsiId> | null = null;

/** Resolves once Google's script is ready (injects it the first time). */
export const loadGoogleIdentity = (): Promise<GsiId> => {
  if (window.google) return Promise.resolve(window.google.accounts.id);
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GSI_SCRIPT;
    script.async = true;
    script.onload = () =>
      window.google ? resolve(window.google.accounts.id) : reject(new Error("GSI missing"));
    script.onerror = () => {
      loading = null;
      reject(new Error("could not load Google sign-in"));
    };
    document.head.appendChild(script);
  });
  return loading;
};

// "The user logged out — don't sign anyone in automatically." Google's own
// disableAutoSelect() only works where its script is loaded (the sign-in pages), so a
// logout from any other page, followed by a reload, would be forgotten and the next
// /login would sign the user straight back in — on a shared store computer, the next
// person lands in their account. The flag lives in localStorage until someone signs in
// with the Google button again.
const SIGNED_OUT_KEY = "shobdo_google_signed_out";

export const rememberGoogleSignOut = (): void => {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // storage unavailable (private mode): fall back to Google's own flag only
  }
  window.google?.accounts.id.disableAutoSelect();
};

export const googleSignedOut = (): boolean => {
  try {
    return localStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

export const forgetGoogleSignOut = (): void => {
  try {
    localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // nothing to forget
  }
};
